import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { ChevronLeft, FileSignature, CheckCircle2 } from "lucide-react";

export default function ConsentForms() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("consent_forms_title"));
  const [data, setData] = useState(undefined);

  useEffect(() => {
    api.get("/consent-forms/mine").then((r) => setData(r.data)).catch(() => setData({ signatures: [], count: 0 }));
  }, []);

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };
  const rows = data?.signatures || [];

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="consent-forms-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/account")} data-testid="consent-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("consent_forms_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("consent_forms_sub")}</p>
        </div>
      </div>

      {data === undefined ? <Spinner label={t("common.loading")} /> : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border p-10 text-center" data-testid="consent-empty">
          <FileSignature className="h-7 w-7 text-muted-foreground" />
          <p className="max-w-sm text-sm text-muted-foreground">{t("consent_forms_empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4" data-testid={`consent-row-${r.id}`}>
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm text-foreground">{r.template_title}</p>
                {r.coach_name ? <p className="text-xs text-muted-foreground">{r.coach_name}</p> : null}
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                  <span>{t("consent_signed_on", { when: fmt(r.signed_at) })}</span>
                  {r.template_version ? <span>· {t("consent_version", { v: r.template_version })}</span> : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
